import * as React from "react";
import { cn } from "@/lib/utils";

interface SubsidyBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Original price before subsidy (e.g. 29). Rendered as a struck-through
   *  pound amount. */
  originalPrice?: number;
  /** Override the promotional label (defaults to "FREE"). */
  promoLabel?: string;
}

/**
 * Promotional badge showing a struck-through original price alongside a "FREE"
 * label and the "Subsidised by Coaley Peak" attribution line.
 */
const SubsidyBadge = React.forwardRef<HTMLDivElement, SubsidyBadgeProps>(
  (
    { originalPrice = 29, promoLabel = "FREE", className, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "inline-flex flex-col items-start gap-1 rounded-md",
        "border border-secondary/40 bg-secondary/[0.08] px-4 py-3",
        className,
      )}
      {...props}
    >
      {/* Price line */}
      <p className="flex items-baseline gap-2">
        <span className="text-sm text-mid line-through" aria-label={`Was ${originalPrice} pounds`}>
          &pound;{originalPrice}
        </span>
        <span className="text-lg font-bold text-primary">{promoLabel}*</span>
      </p>
      <p className="text-[10px] text-mid">*Until August 2026</p>

      {/* Attribution */}
      <p className="flex items-center gap-1.5 text-xs text-mid">
        {/* Coaley Peak icon */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/coaley-peak-icon.svg"
          alt=""
          aria-hidden="true"
          width={20}
          height={20}
          className="rounded-sm"
        />
        <span>
          Subsidised by{" "}
          <span className="font-semibold text-dark">
            Coaley Peak<sup>&reg;</sup>
          </span>
        </span>
      </p>
    </div>
  ),
);

SubsidyBadge.displayName = "SubsidyBadge";

export { SubsidyBadge };
export type { SubsidyBadgeProps };
